import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import { fetchExpenses, fetchAllExpenses, fetchEmployees, submitExpenseApi, decideExpenseApi } from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Button from '../components/Button';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Alert from '../components/Alert';
import Empty from '../components/Empty';
import type { ExpenseStatus } from '../types';
import { formatMinor, sumMinor } from '../lib/money';

const STATUS_VARIANT: Record<ExpenseStatus, 'success' | 'warn' | 'danger' | 'info' | 'neutral'> = {
  paid: 'success', approved: 'info', submitted: 'warn', draft: 'neutral', rejected: 'danger',
};

/**
 * Amounts arrive as minor-unit strings (CR-1); formatting and the single
 * division by 100 live in lib/money.
 */
const fmt = (minor: string | number | null | undefined) =>
  formatMinor(minor, 'INR', { maximumFractionDigits: 0 });

const CATEGORIES = ['Travel', 'Meals', 'Software', 'Hardware', 'Training', 'Office Supplies', 'Other'];

export default function Expenses() {
  const { role, profileId, employeeId, modal, openModal, closeModal, toast } = useAppStore();
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ category: 'Travel', description: '', amount: '' });

  const { data: myExpensesData = [] } = useQuery({
    queryKey: qk.expenses(myId),
    queryFn: () => fetchExpenses(myId),
    enabled: !!employeeId && role === 'ess',
    // Live pushes over SSE (see WebShell's useLiveEvents) invalidate this on
    // decision; this is just a slow safety net in case a push is missed.
    refetchInterval: 60_000,
  });

  const { data: allExpensesData = [] } = useQuery({
    queryKey: ['allExpenses'],
    queryFn: fetchAllExpenses,
    enabled: role !== 'ess',
  });

  const expenses = role === 'ess' ? myExpensesData : allExpensesData;

  const { data: employees = [] } = useQuery({
    queryKey: qk.employees(),
    queryFn: fetchEmployees,
    enabled: role !== 'ess',
  });

  const submitMutation = useMutation({
    mutationFn: submitExpenseApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.expenses(myId) });
      toast('Expense claim submitted');
      closeModal();
    },
    onError: () => toast('Failed to submit expense', 'danger'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => decideExpenseApi(id, 'approved'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.expenses(myId) });
      queryClient.invalidateQueries({ queryKey: ['allExpenses'] });
      toast('Expense approved');
    },
    onError: () => toast('Failed to approve expense', 'danger'),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => decideExpenseApi(id, 'rejected'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.expenses(myId) });
      queryClient.invalidateQueries({ queryKey: ['allExpenses'] });
      toast('Expense rejected', 'warn');
    },
    onError: () => toast('Failed to reject expense', 'danger'),
  });

  const myExpenses = expenses;

  // Summed in integer minor units — never via Number() on each string.
  const total = sumMinor(myExpenses.filter(e => ['approved', 'paid'].includes(e.status)).map(e => e.amountMinor));

  return (
    <div>
      <div className="sk-page-header">
        <h1>Expenses</h1>
        {role === 'ess' && (
          <Button variant="accent" disabled={!employeeId} onClick={() => openModal('new_expense')}>
            <Icon name="plus" size={14} color="white" />
            New claim
          </Button>
        )}
      </div>

      {myExpenses.length > 0 && (
        <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginBottom: 16 }}>
          Total submitted: <strong style={{ color: 'var(--ink)' }}>{fmt(sumMinor(myExpenses.map(e => e.amountMinor)))}</strong>
        </p>
      )}

      <div className="t-card">
        {myExpenses.length === 0 ? (
          <Empty title="No claims yet" description="Submit your first expense to get reimbursed." icon="receipt" />
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                {role !== 'ess' && <th>Employee</th>}
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                {role !== 'ess' && <th></th>}
              </tr>
            </thead>
            <tbody>
              {myExpenses.map((e) => {
                const emp = employees.find(em => em.id === e.employeeId);
                const empDisplay = e.employee ?? (emp ? { firstName: emp.firstName, lastName: emp.lastName } : null);
                return (
                  <tr key={e.id}>
                    {role !== 'ess' && (
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar firstName={empDisplay?.firstName ?? ''} lastName={empDisplay?.lastName} color={emp?.avatarColor} size="sm" />
                          <span>{empDisplay?.firstName} {empDisplay?.lastName}</span>
                        </div>
                      </td>
                    )}
                    <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{e.expenseDate}</td>
                    <td><Chip variant="neutral">{e.category}</Chip></td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>{fmt(e.amountMinor)}</td>
                    <td><Chip variant={STATUS_VARIANT[e.status]} dot>{e.status}</Chip></td>
                    {role !== 'ess' && (
                      <td>
                        {e.status === 'submitted' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <Button variant="success" size="sm" onClick={() => approveMutation.mutate(e.id)}>Approve</Button>
                            <Button variant="danger" size="sm" onClick={() => rejectMutation.mutate(e.id)}>Reject</Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New expense modal */}
      {modal.type === 'new_expense' && (
        <Modal
          title="New expense claim"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal}>Cancel</Button>
              <Button
                variant="accent"
                disabled={submitMutation.isPending}
                onClick={() => {
                  if (!form.description || !form.amount) {
                    toast('Fill all required fields', 'warn'); return;
                  }
                  submitMutation.mutate({
                    employeeId: employeeId,
                    category: form.category,
                    description: form.description,
                    amount: parseFloat(form.amount),
                    currency: 'INR',
                    expenseDate: new Date().toISOString().slice(0, 10),
                  });
                }}
              >Submit claim</Button>
            </>
          }
        >
          <div className="form-row">
            <label>Category</label>
            <select className="t-input" value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label>Description</label>
            <input className="t-input" placeholder="Brief description" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Amount (INR)</label>
            <input type="number" className="t-input" placeholder="0" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
        </Modal>
      )}
    </div>
  );
}
